import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Bank account state for distributor
 * Stores banking information for payment processing
 */
export interface DisBankState {
  bankName: string | null;
  bankCode: string | null;
  accountNumber: string | null;
  accountType: string | null;
  accountHolderName: string | null;
  swiftCode: string | null;
  bankAddress: string | null;
  bankCountry: string | null;
  currency: string | null;
}

const initialState: DisBankState = {
  bankName: null,
  bankCode: null,
  accountNumber: null,
  accountType: null,
  accountHolderName: null,
  swiftCode: null,
  bankAddress: null,
  bankCountry: null,
  currency: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof DisBankState>(field: K) => {
  return (state: DisBankState, action: PayloadAction<DisBankState[K]>) => {
    state[field] = action.payload;
  };
};

const disBankSlice = createSlice({
  name: "disBank",
  initialState,
  reducers: {
    // Bank account field setters
    setBankNameRedux: createFieldSetter("bankName"),
    setBankCodeRedux: createFieldSetter("bankCode"),
    setBankNoRedux: createFieldSetter("accountNumber"),
    setAccountTypeRedux: createFieldSetter("accountType"),
    setAccountHolderNameRedux: createFieldSetter("accountHolderName"),
    setSwiftCodeRedux: createFieldSetter("swiftCode"),
    setBankAddressRedux: createFieldSetter("bankAddress"),
    setBankCountryRedux: createFieldSetter("bankCountry"),
    setCurrencyRedux: createFieldSetter("currency"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateDisBankState: (state, action: PayloadAction<Partial<DisBankState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetDisBankState: () => initialState,
  },
});

export const {
  setBankNameRedux,
  setBankCodeRedux,
  setBankNoRedux,
  setAccountHolderNameRedux,
  setSwiftCodeRedux,
  setAccountTypeRedux,
  setBankAddressRedux,
  setBankCountryRedux,
  setCurrencyRedux,
  hydrateDisBankState,
  resetDisBankState,
} = disBankSlice.actions;

export default disBankSlice.reducer;
